'use client';

import React, { ReactNode, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import { isScrolledIntoView } from 'utils/device';

import { Palette, Transition } from '../Theme';

const MOVEMENT_IN_DURATION_MS = 400;
const MOVEMENT_OUT_DURATION_MS = 1200;

const useStyles = createUseStyles({
    upAnimationExitingClass: {
        '& span': { color: Palette.Green.x10 + ' !important', transition: `all ${Transition.easeInBack}` },
    },
    downAnimationExitingClass: {
        '& span': { color: Palette.Critical.x10 + ' !important', transition: `all ${Transition.easeInBack}` },
    },
    loadingAnimationExitingClass: {
        '& span': { color: Palette.Text.x30 + ' !important', transition: `all ${Transition.easeInBack}` },
    },
    up: {
        '& span': {
            color: Palette.Green.x10 + ' !important',
            opacity: 1 + ' !important',
            transition: `all ${Transition.easeOutBack}`,
        },
    },
    down: {
        '& span': {
            color: Palette.Critical.x10 + ' !important',
            opacity: 1 + ' !important',
            transition: `all ${Transition.easeOutBack}`,
        },
    },
    loading: {
        '& span': {
            color: Palette.Text.x30 + ' !important',
            opacity: 0.4 + ' !important',
            transition: `all ${Transition.easeOutBack}`,
        },
    },
});

interface NumberChangeAnimationWrapperProps {
    number: number;
    animate?: boolean;
    scrollAnimate?: boolean;
    price?: boolean;
    externalTrigger?: boolean;
    children: ReactNode;
    style?: React.CSSProperties;
    className?: string;
}

type AnimationState = 'up' | 'down' | 'loading' | null;
type ExitAnimationState =
    | 'upAnimationExitingClass'
    | 'downAnimationExitingClass'
    | 'loadingAnimationExitingClass'
    | null;

const NumberChangeAnimationWrapper = ({
    number,
    animate = true,
    scrollAnimate = false,
    price = true,
    externalTrigger = false,
    children,
    style,
    className,
}: NumberChangeAnimationWrapperProps): JSX.Element => {
    const classes = useStyles();
    const [timeoutCanAnimate, setTimeoutCanAnimate] = useState(false);

    const [animationState, setAnimationState] = useState<AnimationState>(null);
    const [addAnimationExitingClass, setAddAnimationExitingClass] = useState<ExitAnimationState>(null);
    const [alreadyScrollAnimated, setAlreadyScrollAnimated] = useState(false);
    const [scrolling, setScrolling] = useState(false);
    const [scrollTop, setScrollTop] = useState(0);

    const prevNumber = useRef<number>(number);
    const inputRef = useRef<HTMLSpanElement>(null);

    const triggerAnimation = (direction: AnimationState, exitDirection: ExitAnimationState): void => {
        if (!timeoutCanAnimate || !animate || direction === null) return;

        setAnimationState(direction);
        setTimeout(() => {
            setAnimationState(null);
            setAddAnimationExitingClass(exitDirection);
            setTimeout(() => setAddAnimationExitingClass(null), MOVEMENT_OUT_DURATION_MS);
        }, MOVEMENT_IN_DURATION_MS);
    };

    const triggerLoadingAnimation = (): void => {
        triggerAnimation('loading', 'loadingAnimationExitingClass');
    };

    useEffect(() => {
        // We use a timeout so that there is a delay before the animation is triggered
        let timeout: NodeJS.Timeout | undefined;
        if (animate) {
            timeout = setTimeout(() => setTimeoutCanAnimate(true), 1000);
        } else {
            setTimeoutCanAnimate(false);
        }

        return (): void => {
            if (!!timeout) clearTimeout(timeout);
        };
    }, [animate]);

    useEffect(() => {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const onScroll = (e: any): void => {
            if (!e.target.documentElement) return;
            setScrollTop(e.target.documentElement.scrollTop);
            setScrolling(e.target.documentElement.scrollTop > scrollTop);
        };
        window.addEventListener('scroll', onScroll);

        return (): void => window.removeEventListener('scroll', onScroll);
    }, [scrollTop]);

    useEffect(
        () => {
            if (inputRef?.current && isScrolledIntoView(inputRef?.current) && !alreadyScrollAnimated && scrollAnimate) {
                if (price && number < 50) {
                    triggerAnimation('down', 'downAnimationExitingClass');
                } else if (price && number > 49) {
                    triggerAnimation('up', 'upAnimationExitingClass');
                } else {
                    triggerLoadingAnimation();
                }
                setAlreadyScrollAnimated(true);
            }
            if (inputRef?.current && !isScrolledIntoView(inputRef?.current)) {
                setAlreadyScrollAnimated(false);
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [scrollTop, scrolling],
    );

    useEffect(
        () => {
            if (externalTrigger == false) return;

            if (price && number < 50) {
                triggerAnimation('down', 'downAnimationExitingClass');
            } else if (price && number > 49) {
                triggerAnimation('up', 'upAnimationExitingClass');
            } else {
                triggerLoadingAnimation();
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [externalTrigger],
    );

    useEffect(
        () => {
            if (number > prevNumber.current) triggerAnimation('up', 'upAnimationExitingClass');
            else if (number < prevNumber.current) triggerAnimation('down', 'downAnimationExitingClass');
            prevNumber.current = number;
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [number],
    );

    return (
        <span
            ref={inputRef}
            style={style}
            className={clsx(
                'inline-flex items-center',
                className,
                animationState && classes[animationState],
                addAnimationExitingClass && classes[addAnimationExitingClass],
            )}
        >
            {children}
        </span>
    );
};

export default NumberChangeAnimationWrapper;
